import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, Mail, RefreshCw, Trash2, Send, Loader2, Sparkles, X } from "lucide-react";

import ConsoleShell from "@/components/ConsoleShell";
import api from "@/lib/api";
import { AGENT_VISUALS } from "@/lib/agents";

export default function Briefings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [opened, setOpened] = useState(null); // briefing detail
  const [openedLoading, setOpenedLoading] = useState(false);
  const navigate = useNavigate();

  const refresh = () => {
    setLoading(true);
    api.get("/briefings").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(refresh, []);

  const generateNow = async (send) => {
    setGenerating(true);
    toast.info("Génération en cours… (10 agents en parallèle, ~30-60s)");
    try {
      const { data } = await api.post(`/briefings/generate?send=${send ? "true" : "false"}`, {}, { timeout: 180000 });
      const status = data?.delivery?.email;
      if (send) {
        if (status === "sent") toast.success("Briefing envoyé par email ✉️");
        else toast.error(`Briefing généré mais email échoué : ${data?.delivery?.error?.slice(0, 80) || "erreur inconnue"}`);
      } else {
        toast.success("Briefing généré (non envoyé)");
      }
      refresh();
      // Auto-open the new briefing
      openDetail(data.id);
    } catch (e) {
      toast.error("Échec génération : " + (e?.message || "timeout ?"));
    } finally {
      setGenerating(false);
    }
  };

  const openDetail = async (id) => {
    setOpenedLoading(true);
    try {
      const { data } = await api.get(`/briefings/${id}`);
      setOpened(data);
    } catch {
      toast.error("Impossible de charger le briefing");
    } finally {
      setOpenedLoading(false);
    }
  };

  const remove = async (id, e) => {
    e?.stopPropagation();
    if (!confirm("Supprimer ce briefing ?")) return;
    await api.delete(`/briefings/${id}`);
    refresh();
    if (opened?.id === id) setOpened(null);
  };

  const resend = async (id, e) => {
    e?.stopPropagation();
    try {
      const { data } = await api.get(`/briefings/${id}`);
      // Open detail with note to re-send via main button
      setOpened(data);
      toast.info("Pour renvoyer ce briefing par email, utilisez 'Générer + Envoyer' depuis le bouton principal.");
    } catch {
      toast.error("Briefing introuvable");
    }
  };

  // Used by detail drawer "Envoyer par email" button (re-uses already-stored HTML)
  const resendOpenedByEmail = async () => {
    if (!opened) return;
    toast.info("Renvoi par email…");
    try {
      const { data } = await api.post(`/briefings/${opened.id}/resend`);
      if (data?.delivery?.email === "sent") toast.success("Email renvoyé ✉️");
      else toast.error("Échec : " + (data?.delivery?.error?.slice(0, 80) || "erreur"));
      refresh();
      const fresh = await api.get(`/briefings/${opened.id}`);
      setOpened(fresh.data);
    } catch (e) {
      toast.error("Erreur : " + (e?.message || ""));
    }
  };

  return (
    <ConsoleShell>
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-start justify-between gap-6 mb-10 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Briefings matinaux</div>
            <h1 className="font-display text-4xl tracking-tighter font-semibold" data-testid="briefings-title">
              Vos briefings de 07h00
            </h1>
            <p className="text-zinc-400 mt-3 max-w-2xl">
              Chaque matin à 07h00 (Europe/Paris), vos 10 agents IA font le point sur votre activité et envoient un rapport
              détaillé par email. Vous pouvez aussi en générer un à la demande.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => generateNow(false)}
              disabled={generating}
              data-testid="briefing-generate-only-btn"
              className="border border-white/20 px-4 py-2.5 text-sm hover:bg-white/5 transition flex items-center gap-2 disabled:opacity-50"
            >
              <Sparkles className="w-4 h-4" />
              Générer (sans email)
            </button>
            <button
              onClick={() => generateNow(true)}
              disabled={generating}
              data-testid="briefing-generate-send-btn"
              className="bg-white text-black px-4 py-2.5 text-sm font-medium hover:bg-zinc-200 transition flex items-center gap-2 disabled:opacity-50"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {generating ? "Génération…" : "Générer + Envoyer"}
            </button>
          </div>
        </div>

        {/* Schedule indicator */}
        <div className="mb-8 bg-[#141414] border border-white/10 p-4 flex items-center gap-4 flex-wrap">
          <Calendar className="w-4 h-4 text-emerald-400" />
          <div className="text-sm">
            <span className="font-mono text-zinc-300">07h00 Europe/Paris</span>
            <span className="text-zinc-500"> · tous les jours · </span>
            <span className="text-zinc-300">10 agents en parallèle</span>
            <span className="text-zinc-500"> · livré par email à </span>
            <span className="font-mono text-zinc-300">gabryx@chantix.pro</span>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-20 text-zinc-500">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="bg-[#141414] border border-white/10 p-12 text-center">
            <Sparkles className="w-8 h-8 mx-auto mb-4 text-zinc-600" />
            <div className="font-display text-xl mb-2">Aucun briefing pour l'instant</div>
            <p className="text-zinc-400 text-sm max-w-md mx-auto mb-6">
              Le premier sera généré automatiquement demain à 07h00 — ou cliquez sur "Générer" pour en créer un maintenant.
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="briefings-list">
            {items.map((b) => (
              <li
                key={b.id}
                onClick={() => openDetail(b.id)}
                data-testid={`briefing-item-${b.id}`}
                className="bg-[#141414] border border-white/10 hover:border-white/30 p-5 cursor-pointer transition lift"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <div className="font-display text-xl font-semibold">{b.date_label}</div>
                      <span className={`text-[10px] uppercase tracking-[0.15em] font-mono px-2 py-0.5 ${
                        b.delivery?.email === "sent" ? "bg-emerald-500/10 text-emerald-400" :
                        b.delivery?.email === "failed" ? "bg-red-500/10 text-red-400" :
                        "bg-zinc-500/10 text-zinc-400"
                      }`}>
                        {b.delivery?.email === "sent" ? "✓ envoyé" : b.delivery?.email === "failed" ? "✗ email ko" : "non envoyé"}
                      </span>
                    </div>
                    <div className="text-xs text-zinc-500 font-mono">
                      {b.sections?.length || 0} sections · {b.chantiers_count || 0} chantiers · généré à{" "}
                      {new Date(b.created_at).toLocaleString("fr-FR")}
                    </div>
                  </div>
                  <button
                    onClick={(e) => remove(b.id, e)}
                    data-testid={`briefing-delete-${b.id}`}
                    className="text-zinc-500 hover:text-red-400 p-1.5"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Detail drawer */}
      {opened && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex justify-end" onClick={() => setOpened(null)}>
          <div
            className="bg-[#0a0a0a] border-l border-white/10 w-full max-w-3xl h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="briefing-detail"
          >
            <div className="sticky top-0 glass border-b border-white/10 p-5 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Briefing</div>
                <div className="font-display text-2xl font-semibold">{opened.date_label}</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={resendOpenedByEmail}
                  data-testid="briefing-resend-btn"
                  className="text-xs border border-white/20 px-3 py-1.5 hover:bg-white/5 transition flex items-center gap-1.5"
                  title="Renvoyer ce briefing par email (sans regénérer)"
                >
                  <Mail className="w-3.5 h-3.5" /> Renvoyer
                </button>
                <button onClick={() => setOpened(null)} className="p-2 hover:bg-white/5" data-testid="briefing-detail-close">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {openedLoading ? (
              <div className="p-12 text-center text-zinc-500">Chargement…</div>
            ) : (
              <div className="p-6 space-y-4">
                {/* Stats snapshot */}
                {opened.stats_snapshot && (
                  <div className="grid grid-cols-4 gap-3 mb-2">
                    <SnapshotCell label="Users" value={opened.stats_snapshot.users?.total} />
                    <SnapshotCell label="Chantiers" value={opened.stats_snapshot.chantiers?.total} />
                    <SnapshotCell label="Pilotes" value={`${opened.stats_snapshot.pilots?.total}/${opened.stats_snapshot.pilots?.quota}`} />
                    <SnapshotCell label="Feedback" value={opened.stats_snapshot.feedback?.total} />
                  </div>
                )}

                {/* Sections */}
                {opened.sections?.map((s) => {
                  const v = AGENT_VISUALS[s.code];
                  return (
                    <div
                      key={s.code}
                      className="bg-[#141414] border-l-2 p-5"
                      style={{ borderLeftColor: s.color }}
                      data-testid={`briefing-section-${s.code}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden" style={{ boxShadow: `0 0 0 1.5px ${s.color}` }}>
                            {v?.avatar && <img src={v.avatar} alt="" className="w-full h-full object-cover" />}
                          </div>
                          <div>
                            <div className="font-display font-semibold text-lg">{s.name}</div>
                            <div className="text-[11px] text-zinc-500 uppercase tracking-[0.15em]">{s.role}</div>
                          </div>
                        </div>
                        <span className="text-[10px] uppercase tracking-[0.15em] font-mono" style={{ color: s.color }}>
                          {s.coverage}
                        </span>
                      </div>
                      <div className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">
                        <SectionMarkdown content={s.content} />
                      </div>
                    </div>
                  );
                })}

                {/* Delivery status */}
                <div className="bg-[#141414] border border-white/10 p-4 text-xs font-mono">
                  <div className="text-zinc-500 uppercase tracking-[0.2em] text-[10px] mb-2">Email delivery</div>
                  <div className="text-zinc-300">
                    Statut : {opened.delivery?.email || "—"}{" "}
                    {opened.delivery?.recipient && `→ ${opened.delivery.recipient}`}
                  </div>
                  {opened.delivery?.error && (
                    <div className="text-red-400 mt-1 break-all">{opened.delivery.error}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </ConsoleShell>
  );
}

function SnapshotCell({ label, value }) {
  return (
    <div className="bg-[#141414] border border-white/10 p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="font-display text-xl font-semibold mt-1">{value ?? "—"}</div>
    </div>
  );
}

function SectionMarkdown({ content }) {
  // Minimal markdown: **bold**, headings, bullets, line breaks
  const lines = content.split("\n");
  const blocks = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length) {
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="list-disc ml-5 my-2 space-y-1">
          {listBuf.map((l, i) => <li key={i} dangerouslySetInnerHTML={{ __html: inline(l) }} />)}
        </ul>
      );
      listBuf = [];
    }
  };
  lines.forEach((line, idx) => {
    const t = line.trim();
    if (/^[-*]\s+/.test(t)) listBuf.push(t.replace(/^[-*]\s+/, ""));
    else if (/^#{1,4}\s+/.test(t)) {
      flushList();
      const text = t.replace(/^#{1,4}\s+/, "");
      blocks.push(<div key={idx} className="font-display text-base font-semibold mt-3 mb-1" dangerouslySetInnerHTML={{ __html: inline(text) }} />);
    } else if (t === "") {
      flushList();
      blocks.push(<div key={idx} className="h-2" />);
    } else {
      flushList();
      blocks.push(<p key={idx} className="my-1" dangerouslySetInnerHTML={{ __html: inline(line) }} />);
    }
  });
  flushList();
  return <>{blocks}</>;
}

function inline(s) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong class='text-white'>$1</strong>")
    .replace(/`([^`]+)`/g, "<code class='font-mono text-xs bg-white/10 px-1.5 py-0.5 rounded'>$1</code>");
}
